import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { useRecoilValue } from 'recoil';

import {
  ArrowDownIcon,
  ArrowUpIcon,
  ChevronDoubleDownIcon,
  ChevronDoubleUpIcon,
  PlusIcon,
} from '@heroicons/react/outline';
import {
  simpleBugoBrandListDataAtom,
  simpleBugoBrandListStatusAtom,
} from '@shared/api/bugoBrand/bugoBrand.atom';
import { useSimpleBugoBrandListHook } from '@shared/api/bugoBrand/bugoBrand.hook';
import {
  giftDeliveryCategoryListDataAtom,
  giftDeliveryCategoryListStatusAtom,
} from '@shared/api/giftDeliveryCategory/giftDeliveryCategory.atom';
import { useGiftDeliveryCategoryListHook } from '@shared/api/giftDeliveryCategory/giftDeliveryCategory.hook';
import {
  giftItemListDataAtom,
  giftItemListStatusAtom,
} from '@shared/api/giftItem/giftItem.atom';
import { useGiftItemListHook } from '@shared/api/giftItem/giftItem.hook';
import {
  giftItemBrandListDataAtom,
  giftItemBrandListStatusAtom,
} from '@shared/api/giftItemBrand/giftItemBrand.atom';
import { useGiftItemBrandListHook } from '@shared/api/giftItemBrand/giftItemBrand.hook';
import {
  deleteGiftStoreBanner,
  fetchAdminGiftStoreBanners,
  isGiftStoreBannerUnsupported,
  reorderGiftStoreBanners,
  updateGiftStoreBanner,
} from '@shared/api/giftStoreBanner/giftStoreBanner.controller';
import {
  GiftStoreBannerData,
  GiftStoreBannerLink,
  GiftStoreBannerLinkType,
} from '@shared/api/giftStoreBanner/giftStoreBanner.interface';
import { Loading } from '@shared/components/Loading';
import { useAuth } from '@shared/state/hooks/useAuth';
import { LowerRole } from '@shared/types';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import {
  bugoBrandIdsWithGiftItems,
  bugoBrandsMissingBannerLink,
  formatKstShort,
  GIFT_STORE_BANNER_STATUS_LABEL,
  getGiftStoreBannerStatus,
  GiftStoreBannerStatus,
  refId,
} from '@shared/utils/giftStoreBanner.util';

import {
  bugoBrandLabel,
  GiftStoreBannerFormModal,
} from '../components/storeBanner/GiftStoreBannerFormModal';
import { useAdminTitleHook } from '../hooks/useAdminTitleHook';

const STATUS_CHIP_CLASS: Record<GiftStoreBannerStatus, string> = {
  Live: 'bg-green-50 text-green-700 ring-green-200',
  Scheduled: 'bg-blue-50 text-blue-700 ring-blue-200',
  Ended: 'bg-gray-100 text-gray-500 ring-gray-200',
  Off: 'bg-gray-100 text-gray-500 ring-gray-200',
};

type LoadState = 'loading' | 'ready' | 'unsupported' | 'error';

const secondaryButton =
  'inline-flex items-center justify-center gap-2 rounded border border-gray-300 bg-white px-3.5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40';
const primaryButton =
  'inline-flex items-center justify-center gap-2 rounded bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-40';

/** 답례품 스토어 메인 배너 관리 (관리자 홈 /admin/gift-store-banner) — docs/gift-store-banner-spec.md */
export const GiftStoreBannerPage: FC = function GiftStoreBannerPage() {
  useAdminTitleHook('스토어 배너');
  const { isAdmin } = useAuth();
  // GiftAgent가 주소로 들어와도 admin 조회를 쏘지 않게 먼저 거른다
  if (!isAdmin()) return <div className="p-8 text-sm text-gray-500">관리자 전용</div>;
  return <GiftStoreBannerManager />;
};

const GiftStoreBannerManager: FC = function GiftStoreBannerManager() {
  // 경고·후보 계산용 참조 데이터 (모두 기존 admin 조회)
  useSimpleBugoBrandListHook();
  useGiftItemListHook(LowerRole.Admin);
  useGiftItemBrandListHook(LowerRole.Admin);
  useGiftDeliveryCategoryListHook(LowerRole.Admin);
  const bugoBrands = useRecoilValue(simpleBugoBrandListDataAtom) ?? [];
  const bugoBrandStatus = useRecoilValue(simpleBugoBrandListStatusAtom);
  const giftItems = useRecoilValue(giftItemListDataAtom) ?? [];
  const giftItemStatus = useRecoilValue(giftItemListStatusAtom);
  const itemBrands = useRecoilValue(giftItemBrandListDataAtom);
  const itemBrandStatus = useRecoilValue(giftItemBrandListStatusAtom);
  const categories = useRecoilValue(giftDeliveryCategoryListDataAtom);
  const categoryStatus = useRecoilValue(giftDeliveryCategoryListStatusAtom);
  const refReady = [bugoBrandStatus, giftItemStatus, itemBrandStatus, categoryStatus].every(
    (s) => s === 'success',
  );

  const [banners, setBanners] = useState<GiftStoreBannerData[]>([]);
  const [loadState, setLoadState] = useState<LoadState>('loading');
  const load = useCallback(async () => {
    try {
      setBanners(await fetchAdminGiftStoreBanners());
      setLoadState('ready');
    } catch (error) {
      setLoadState(isGiftStoreBannerUnsupported(error) ? 'unsupported' : 'error');
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  // 모달: undefined=닫힘, null=신규, 배너=수정
  const [editing, setEditing] = useState<GiftStoreBannerData | null | undefined>(undefined);
  const [busyId, setBusyId] = useState<string | null>(null);

  // 순서 편집 — null이면 일반 목록
  const [reorderIds, setReorderIds] = useState<string[] | null>(null);
  const [isSavingOrder, setIsSavingOrder] = useState(false);
  const reorderList = useMemo(
    () =>
      (reorderIds ?? [])
        .map((id) => banners.find((b) => b._id === id))
        .filter((b): b is GiftStoreBannerData => !!b),
    [reorderIds, banners],
  );
  const orderChanged = Boolean(reorderIds?.some((id, i) => id !== banners[i]?._id));
  const moveBanner = (from: number, to: number) =>
    setReorderIds((prev) => {
      if (!prev || to < 0 || to >= prev.length) return prev;
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved!);
      return next;
    });
  const saveReorder = async () => {
    if (!reorderIds) return;
    setIsSavingOrder(true);
    try {
      await reorderGiftStoreBanners(reorderIds);
      setReorderIds(null);
      await load();
    } catch (error) {
      console.error(error);
      alert('순서 저장에 실패했습니다.');
    } finally {
      setIsSavingOrder(false);
    }
  };

  const giftBugoBrandIds = useMemo(
    () => Array.from(bugoBrandIdsWithGiftItems(giftItems)),
    [giftItems],
  );
  const bugoBrandById = useMemo(
    () => new Map(bugoBrands.map((b) => [b._id, b])),
    [bugoBrands],
  );

  const linkLabel = (link: GiftStoreBannerLink | null | undefined): string => {
    if (!link || link.type === GiftStoreBannerLinkType.None) return '클릭 없음';
    if (link.type === GiftStoreBannerLinkType.GiftItemBrand) {
      const name = itemBrands.find((b) => b._id === link.giftItemBrand)?.name;
      return `브랜드 · ${name ?? '(삭제된 브랜드)'}`;
    }
    if (link.type === GiftStoreBannerLinkType.DeliveryCategory) {
      const name = categories.find((c) => c._id === link.giftDeliveryCategory)?.name;
      return `분류 · ${name ?? '(삭제된 분류)'}${link.subCategory ? ` > ${link.subCategory}` : ''}`;
    }
    const info = giftItems.find((i) => i.giftItemInfo?._id === link.giftItemInfo)?.giftItemInfo;
    return `상품 · ${info?.name ?? '(판매 중 아님)'}`;
  };

  const targetIdsOf = (banner: GiftStoreBannerData): string[] =>
    (banner.bugoBrands ?? []).map((b) => refId(b)).filter((id): id is string => !!id);

  const toggleEnabled = async (banner: GiftStoreBannerData) => {
    setBusyId(banner._id);
    try {
      await updateGiftStoreBanner(banner._id, { isEnabled: banner.isEnabled === false });
      await load();
    } catch (error) {
      console.error(error);
      alert('변경에 실패했습니다.');
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (banner: GiftStoreBannerData) => {
    if (!window.confirm(`"${banner.title}" 배너를 삭제할까요?`)) return;
    setBusyId(banner._id);
    try {
      await deleteGiftStoreBanner(banner._id);
      await load();
    } catch (error) {
      console.error(error);
      alert('삭제에 실패했습니다.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 text-gray-900">
      <div>
        {/* 제목은 관리자 상단바(useAdminTitleHook)가 보여준다 */}
        <header className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-gray-500">
            답례품 스토어 메인 상단 · 노출 중인 배너가 없으면 기본 이미지
          </p>
          {loadState === 'ready' &&
            (reorderIds ? (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setReorderIds(null)}
                  disabled={isSavingOrder}
                  className={secondaryButton}
                >
                  취소
                </button>
                <button
                  type="button"
                  onClick={saveReorder}
                  disabled={isSavingOrder || !orderChanged}
                  className={primaryButton}
                >
                  {isSavingOrder ? '저장 중…' : '순서 저장'}
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                {banners.length > 1 && (
                  <button
                    type="button"
                    onClick={() => setReorderIds(banners.map((b) => b._id))}
                    className={secondaryButton}
                  >
                    순서 변경
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setEditing(null)}
                  disabled={!refReady}
                  className={primaryButton}
                >
                  <PlusIcon aria-hidden className="h-4 w-4" />
                  배너 추가
                </button>
              </div>
            ))}
        </header>

        {loadState === 'loading' && (
          <div className="center-box py-20">
            <Loading />
          </div>
        )}
        {loadState === 'unsupported' && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-6 text-sm text-amber-800">
            서버에 배너 기능이 아직 배포되지 않았습니다. (스토어엔 기본 이미지가 보입니다)
          </div>
        )}
        {loadState === 'error' && (
          <div className="rounded-md border border-gray-200 bg-white p-6 text-sm text-gray-600">
            배너 목록을 불러오지 못했습니다.{' '}
            <button type="button" onClick={load} className="underline">
              다시 시도
            </button>
          </div>
        )}

        {loadState === 'ready' && reorderIds && (
          <ul
            aria-label="배너 표시 순서"
            className="rounded-md border border-gray-200 bg-white"
          >
            {reorderList.map((banner, index) => (
              <li
                key={banner._id}
                className="flex items-center gap-3 border-b border-gray-100 px-4 py-3 last:border-b-0"
              >
                <span className="w-7 shrink-0 text-sm tabular-nums text-gray-400">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <span className="min-w-0 flex-1 truncate text-sm font-medium">
                  {banner.title}
                </span>
                <div className="flex shrink-0 gap-1">
                  {[
                    { label: '맨 위로', to: 0, icon: ChevronDoubleUpIcon, off: index === 0 },
                    { label: '위로', to: index - 1, icon: ArrowUpIcon, off: index === 0 },
                    {
                      label: '아래로',
                      to: index + 1,
                      icon: ArrowDownIcon,
                      off: index === reorderList.length - 1,
                    },
                    {
                      label: '맨 아래로',
                      to: reorderList.length - 1,
                      icon: ChevronDoubleDownIcon,
                      off: index === reorderList.length - 1,
                    },
                  ].map(({ label, to, icon: Icon, off }) => (
                    <button
                      key={label}
                      type="button"
                      title={label}
                      aria-label={`${banner.title} ${label}`}
                      onClick={() => moveBanner(index, to)}
                      disabled={off || isSavingOrder}
                      className="rounded border border-gray-200 p-2 text-gray-600 hover:bg-gray-50 disabled:opacity-25"
                    >
                      <Icon aria-hidden className="h-4 w-4" />
                    </button>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        )}

        {loadState === 'ready' && !reorderIds && (
          <>
            {banners.length === 0 ? (
              <div className="rounded-md border border-dashed border-gray-300 bg-white p-10 text-center text-sm text-gray-500">
                등록된 배너 없음 · 스토어엔 기본 이미지
              </div>
            ) : (
              <ul className="space-y-3">
                {banners.map((banner, index) => {
                  const status = getGiftStoreBannerStatus(banner);
                  const targets = targetIdsOf(banner);
                  const missing = refReady
                    ? bugoBrandsMissingBannerLink(
                        banner.link,
                        targets.length ? targets : giftBugoBrandIds,
                        giftItems,
                      )
                    : [];
                  const imageUrl =
                    banner.image && typeof banner.image === 'object'
                      ? fileBucketUrl(banner.image.url, 'w400')
                      : '';
                  return (
                    <li
                      key={banner._id}
                      className="flex flex-col gap-3 rounded-md border border-gray-200 bg-white p-3 sm:flex-row"
                    >
                      <div className="flex shrink-0 items-start gap-3">
                        <span className="w-6 pt-1 text-sm tabular-nums text-gray-400">
                          {index + 1}
                        </span>
                        <div className="w-full overflow-hidden rounded bg-gray-100 sm:w-56">
                          {imageUrl && (
                            <img
                              src={imageUrl}
                              alt={banner.alt || banner.title}
                              className={`w-full ${status === 'Live' ? '' : 'opacity-50'}`}
                            />
                          )}
                        </div>
                      </div>
                      <div className="min-w-0 flex-1 space-y-1 text-sm">
                        <div className="flex flex-wrap items-center gap-2">
                          <span
                            className={`rounded px-1.5 py-0.5 text-xs font-medium ring-1 ${STATUS_CHIP_CLASS[status]}`}
                          >
                            {GIFT_STORE_BANNER_STATUS_LABEL[status]}
                          </span>
                          <span className="font-semibold">{banner.title}</span>
                        </div>
                        <p className="text-gray-600">
                          {banner.startAt || banner.endAt
                            ? `${formatKstShort(banner.startAt) || '즉시'} ~ ${
                                formatKstShort(banner.endAt) || '무기한'
                              }`
                            : '기간 없음'}
                        </p>
                        <p className="text-gray-600">{linkLabel(banner.link)}</p>
                        <p className="text-gray-600">
                          {targets.length === 0
                            ? '전체 상조'
                            : targets
                                .map((id) => bugoBrandLabel(bugoBrandById.get(id), id))
                                .join(', ')}
                        </p>
                        {missing.length > 0 && (
                          <p className="text-xs text-amber-700">
                            연결 대상 없음:{' '}
                            {missing
                              .map((id) => bugoBrandLabel(bugoBrandById.get(id), id))
                              .join(', ')}
                          </p>
                        )}
                      </div>
                      <div className="flex shrink-0 items-start gap-2">
                        <button
                          type="button"
                          onClick={() => toggleEnabled(banner)}
                          disabled={busyId === banner._id}
                          className={secondaryButton}
                        >
                          {banner.isEnabled === false ? '켜기' : '끄기'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditing(banner)}
                          disabled={!refReady}
                          className={secondaryButton}
                        >
                          수정
                        </button>
                        <button
                          type="button"
                          onClick={() => remove(banner)}
                          disabled={busyId === banner._id}
                          className={`${secondaryButton} text-red-600`}
                        >
                          삭제
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}
      </div>

      {editing !== undefined && (
        <GiftStoreBannerFormModal
          banner={editing}
          bugoBrands={bugoBrands}
          giftItems={giftItems}
          itemBrands={itemBrands}
          categories={categories}
          onClose={() => setEditing(undefined)}
          onSaved={async () => {
            setEditing(undefined);
            await load();
          }}
        />
      )}
    </div>
  );
};
